import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { SEOHead } from '@/components/SEOHead';
import { Layout } from '@/components/layout/Layout';
import { ProjectAdsSidebar } from '@/components/property/ProjectAdsSidebar';
import { PropertyCard } from '@/components/property/PropertyCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import {
  LandPlot, MapPin, Search, Compass, ChevronDown, X,
  LayoutGrid, List, Home, ChevronRight as ChevronRightIcon
} from 'lucide-react';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice as fmtPrice } from '@/lib/formatPrice';

const CITIES = ['Addis Ababa', 'Adama', 'Hawassa', 'Bahir Dar', 'Mekelle', 'Dire Dawa', 'Jimma', 'Gondar'];
const PURPOSE_OPTIONS = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'mixed', label: 'Mixed Use' },
  { value: 'agricultural', label: 'Agricultural' },
];

export default function Plots() {
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState<string | undefined>();
  const [purposeFilter, setPurposeFilter] = useState<string | undefined>();
  const [listingTypeFilter, setListingTypeFilter] = useState<string | undefined>();
  const [minPrice, setMinPrice] = useState<string | undefined>();
  const [maxPrice, setMaxPrice] = useState<string | undefined>();
  const [minArea, setMinArea] = useState<string | undefined>();
  const [maxArea, setMaxArea] = useState<string | undefined>();
  const [sortBy, setSortBy] = useState('newest');
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');

  const { data: plots, isLoading } = useQuery({
    queryKey: ['public-plots'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plots')
        .select('*, plot_images(url, is_primary)')
        .eq('approval_status', 'approved')
        .eq('status', 'active')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const getName = (val: any) => typeof val === 'object' ? (val?.en || '') : (val || '');

  const filtered = (plots || []).filter((plot: any) => {
    const title = getName(plot.title).toLowerCase();
    const desc = getName(plot.description).toLowerCase();
    const q = searchQuery.toLowerCase();
    if (q && !title.includes(q) && !desc.includes(q) && !plot.city?.toLowerCase().includes(q)) return false;
    if (cityFilter && plot.city !== cityFilter) return false;
    if (purposeFilter && plot.plot_purpose !== purposeFilter) return false;
    if (listingTypeFilter && plot.listing_type !== listingTypeFilter) return false;
    if (minPrice && plot.price < Number(minPrice)) return false;
    if (maxPrice && plot.price > Number(maxPrice)) return false;
    if (minArea && (plot.area_sqm || 0) < Number(minArea)) return false;
    if (maxArea && (plot.area_sqm || 0) > Number(maxArea)) return false;
    return true;
  }).sort((a: any, b: any) => {
    if (sortBy === 'price_asc') return a.price - b.price;
    if (sortBy === 'price_desc') return b.price - a.price;
    if (sortBy === 'area') return (b.area_sqm || 0) - (a.area_sqm || 0);
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const { displayCurrency, rate } = useCurrency();
  const formatPrice = (price: number, currency: string) => fmtPrice(price, currency, displayCurrency, rate);

  const activeFilterCount = [cityFilter, purposeFilter, listingTypeFilter, minPrice || maxPrice, minArea || maxArea].filter(Boolean).length;

  const clearAll = () => {
    setCityFilter(undefined); setPurposeFilter(undefined); setListingTypeFilter(undefined);
    setMinPrice(undefined); setMaxPrice(undefined); setMinArea(undefined); setMaxArea(undefined);
  };

  return (
    <Layout>
      <SEOHead
        title="Land & Plots for Sale in Ethiopia — Residential & Commercial"
        description="Find land and plots for sale across Ethiopia. Residential, commercial and mixed-use plots in Addis Ababa, Adama, Hawassa and more cities."
        canonical="/plots"
      />
      <div className="border-b bg-muted/30">
        <div className="container py-4">
          <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRightIcon className="h-3 w-3" />
            <span className="text-foreground font-medium">Plots & Land</span>
          </nav>
          <div className="flex items-center justify-between">
            <h1 className="font-display text-xl md:text-2xl font-bold">Plots & Land</h1>
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{filtered.length}</span> plots found
            </p>
          </div>
        </div>
      </div>

      {/* Sticky Filter Bar */}
      <div className="sticky top-[56px] md:top-[64px] lg:top-[117px] z-30 bg-background border-b shadow-sm">
        <div className="container">
          <div className="flex items-center gap-2 py-3 overflow-x-auto scrollbar-hide">
            {/* Search */}
            <div className="relative shrink-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input placeholder="Search plots..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="h-9 w-48 pl-8 text-xs" />
            </div>

            {/* City */}
            <Select value={cityFilter || 'all'} onValueChange={v => setCityFilter(v === 'all' ? undefined : v)}>
              <SelectTrigger className="h-9 w-auto min-w-[130px] text-xs shrink-0 gap-1.5">
                <Home className="h-3.5 w-3.5 text-muted-foreground" />
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {CITIES.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Purpose */}
            <Select value={purposeFilter || 'all'} onValueChange={v => setPurposeFilter(v === 'all' ? undefined : v)}>
              <SelectTrigger className="h-9 w-auto min-w-[120px] text-xs shrink-0 gap-1.5">
                <SelectValue placeholder="Purpose" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Purposes</SelectItem>
                {PURPOSE_OPTIONS.map(p => <SelectItem key={p.value} value={p.value} className="text-xs">{p.label}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Listing Type */}
            <Select value={listingTypeFilter || 'all'} onValueChange={v => setListingTypeFilter(v === 'all' ? undefined : v)}>
              <SelectTrigger className="h-9 w-auto min-w-[110px] text-xs shrink-0 gap-1.5">
                <SelectValue placeholder="For Sale/Rent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Sale & Rent</SelectItem>
                <SelectItem value="sale" className="text-xs">For Sale</SelectItem>
                <SelectItem value="rent" className="text-xs">For Rent</SelectItem>
              </SelectContent>
            </Select>

            {/* Price Range */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 text-xs shrink-0 gap-1.5">
                  {minPrice || maxPrice ? `${minPrice || '0'} - ${maxPrice || '∞'} ETB` : 'Price Range'}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="start">
                <p className="text-xs font-semibold mb-2">Price Range (ETB)</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" placeholder="Min" className="h-9 text-xs" value={minPrice || ''} onChange={e => setMinPrice(e.target.value || undefined)} />
                  <Input type="number" placeholder="Max" className="h-9 text-xs" value={maxPrice || ''} onChange={e => setMaxPrice(e.target.value || undefined)} />
                </div>
                <div className="flex gap-1.5 mt-2">
                  {[{ label: '<1M', min: undefined, max: '1000000' }, { label: '1-5M', min: '1000000', max: '5000000' }, { label: '5-10M', min: '5000000', max: '10000000' }, { label: '10M+', min: '10000000', max: undefined }].map(p => (
                    <Button key={p.label} variant="outline" size="sm" className="h-7 text-[10px] flex-1" onClick={() => { setMinPrice(p.min); setMaxPrice(p.max); }}>
                      {p.label}
                    </Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Area */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 text-xs shrink-0 gap-1.5">
                  {minArea || maxArea ? `${minArea || '0'} - ${maxArea || '∞'} m²` : 'Area (m²)'}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-3" align="start">
                <p className="text-xs font-semibold mb-2">Area (m²)</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" placeholder="Min" className="h-9 text-xs" value={minArea || ''} onChange={e => setMinArea(e.target.value || undefined)} />
                  <Input type="number" placeholder="Max" className="h-9 text-xs" value={maxArea || ''} onChange={e => setMaxArea(e.target.value || undefined)} />
                </div>
              </PopoverContent>
            </Popover>

            <div className="h-6 w-px bg-border shrink-0 mx-1" />

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-9 w-auto min-w-[130px] text-xs shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
                <SelectItem value="area">Largest Area</SelectItem>
              </SelectContent>
            </Select>

            {/* Layout Toggle */}
            <div className="hidden md:flex border rounded-lg overflow-hidden shrink-0 ml-auto">
              <Button variant={layout === 'grid' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-none" onClick={() => setLayout('grid')}>
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button variant={layout === 'list' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-none" onClick={() => setLayout('list')}>
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Active Filter Chips */}
          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 pb-3 overflow-x-auto scrollbar-hide">
              <span className="text-[10px] text-muted-foreground shrink-0 uppercase tracking-wider font-medium">Active:</span>
              {cityFilter && (
                <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">{cityFilter}<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setCityFilter(undefined)} /></Badge>
              )}
              {purposeFilter && (
                <Badge variant="secondary" className="text-[10px] gap-1 capitalize shrink-0">{purposeFilter}<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setPurposeFilter(undefined)} /></Badge>
              )}
              {listingTypeFilter && (
                <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">{listingTypeFilter === 'sale' ? 'For Sale' : 'For Rent'}<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setListingTypeFilter(undefined)} /></Badge>
              )}
              {(minPrice || maxPrice) && (
                <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">{minPrice || '0'} - {maxPrice || '∞'} ETB<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => { setMinPrice(undefined); setMaxPrice(undefined); }} /></Badge>
              )}
              {(minArea || maxArea) && (
                <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">{minArea || '0'} - {maxArea || '∞'} m²<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => { setMinArea(undefined); setMaxArea(undefined); }} /></Badge>
              )}
              <Button variant="ghost" size="sm" className="h-6 text-[10px] text-destructive shrink-0" onClick={clearAll}>Clear All</Button>
            </div>
          )}
        </div>
      </div>

      {/* Grid + Sidebar */}
      <div className="container py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
          <div>
            {isLoading ? (
              <div className={`grid gap-4 ${layout === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
                {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-xl" />)}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20">
                <LandPlot className="h-14 w-14 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="font-semibold text-lg mb-1">No plots found</h3>
                <p className="text-muted-foreground text-sm">Try adjusting your filters or search query.</p>
              </div>
            ) : (
              <div className={`grid gap-4 ${layout === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`}>
                {filtered.map((plot: any) => (
                  <PropertyCard key={plot.id} property={plot} layout={layout} cardType="plot" />
                ))}
              </div>
            )}
          </div>
          <aside className="hidden lg:block">
            <div className="sticky top-[140px] lg:top-[200px]">
              <ProjectAdsSidebar />
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}
